import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import usePageTitle from "@/hooks/usePageTitle";
import SiftraBadge from "@/components/SiftraBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage({ onLogin }) {
  usePageTitle("Login");
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) {
      toast.error("Please enter the team passcode");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify`, { passcode });
      if (response.data.success) {
        toast.success("Welcome to AGCO UTM Builder");
        onLogin();
      }
    } catch (error) {
      toast.error("Invalid passcode. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Image */}
      <div className="hidden lg:flex lg:w-1/2 login-image relative">
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 p-12 flex flex-col justify-end text-white">
          <h2 className="text-4xl font-bold mb-4 leading-tight">
            AGCO UTM<br />Builder
          </h2>
          <p className="text-white/70 text-sm max-w-md">
            Generate standardized UTM links and maintain consistency across all global marketing campaigns.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-12">
            <h1 className="text-3xl font-bold tracking-tight text-zinc-950">
              AGCO<span className="text-[#AE132A]"> UTM Builder</span>
            </h1>
            <p className="text-xs text-zinc-500 mt-2 uppercase tracking-widest">
              Corporate Link Governance
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
                Team Passcode
              </label>
              <div className="relative">
                <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  type="password"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  placeholder="Enter your team passcode"
                  className="input-with-icon"
                  data-testid="passcode-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="login-btn"
            >
              {loading ? (
                "Verifying..."
              ) : (
                <>
                  Access Platform
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-12 text-xs text-zinc-400 text-center">
            Contact <a href="mailto:richard.sneath@agcocorp.com" className="text-[#AE132A] hover:underline">richard.sneath@agcocorp.com</a> for access credentials.
          </p>
        </div>
      </div>
      <SiftraBadge />
    </div>
  );
}
